/**
 * Detailed logging utility for Supabase authentication debugging
 * Tracks all auth calls, API responses, and network failures
 */

const LOG_LEVELS = {
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
  DEBUG: 'DEBUG'
};

const LOG_CATEGORIES = {
  AUTH: 'AUTH',
  API: 'API',
  NETWORK: 'NETWORK',
  VALIDATION: 'VALIDATION'
};

class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100;
  }

  _formatTimestamp() {
    return new Date()?.toISOString();
  }

  _createLogEntry(level, category, message, data = null) {
    const entry = {
      timestamp: this._formatTimestamp(),
      level,
      category,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : null
    };

    this.logs?.push(entry);
    if (this.logs?.length > this.maxLogs) {
      this.logs?.shift();
    }

    return entry;
  }

  _consoleLog(entry) {
    const prefix = `[${entry?.timestamp}] [${entry?.level}] [${entry?.category}]`;
    const style = this._getConsoleStyle(entry?.level);

    console.log(`%c${prefix}`, style, entry?.message);
    if (entry?.data) {
      console.log('Data:', entry?.data);
    }
  }

  _getConsoleStyle(level) {
    const styles = {
      INFO: 'color: #3B82F6; font-weight: bold',
      WARN: 'color: #F59E0B; font-weight: bold',
      ERROR: 'color: #EF4444; font-weight: bold',
      DEBUG: 'color: #8B5CF6; font-weight: bold'
    };
    return styles?.[level] || '';
  }

  info(category, message, data) {
    const entry = this._createLogEntry(LOG_LEVELS?.INFO, category, message, data);
    this._consoleLog(entry);
  }

  warn(category, message, data) {
    const entry = this._createLogEntry(LOG_LEVELS?.WARN, category, message, data);
    this._consoleLog(entry);
  }

  error(category, message, data) {
    const entry = this._createLogEntry(LOG_LEVELS?.ERROR, category, message, data);
    this._consoleLog(entry);
  }

  debug(category, message, data) {
    const entry = this._createLogEntry(LOG_LEVELS?.DEBUG, category, message, data);
    this._consoleLog(entry);
  }

  // Specialized auth logging methods
  authAttempt(email) {
    this.info(LOG_CATEGORIES?.AUTH, 'Authentication attempt started', { email });
  }

  authRequest(method, params) {
    this.debug(LOG_CATEGORIES?.AUTH, `Auth request: ${method}`, {
      method,
      params: { ...params, password: params?.password ? '***REDACTED***' : undefined }
    });
  }

  authResponse(method, success, data) {
    if (success) {
      this.info(LOG_CATEGORIES?.AUTH, `Auth response: ${method} - SUCCESS`, {
        userId: data?.user?.id,
        email: data?.user?.email,
        role: data?.user?.user_metadata?.role
      });
    } else {
      this.error(LOG_CATEGORIES?.AUTH, `Auth response: ${method} - FAILED`, data);
    }
  }

  authError(method, error) {
    this.error(LOG_CATEGORIES?.AUTH, `Auth error: ${method}`, {
      message: error?.message,
      status: error?.status,
      code: error?.code,
      details: error?.details,
      hint: error?.hint,
      stack: error?.stack
    });
  }

  networkError(operation, error) {
    this.error(LOG_CATEGORIES?.NETWORK, `Network failure: ${operation}`, {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      isNetworkError: error instanceof TypeError || error?.message?.includes('fetch')
    });
  }

  apiCall(endpoint, method, params) {
    this.debug(LOG_CATEGORIES?.API, `API call: ${method} ${endpoint}`, params);
  }

  apiResponse(endpoint, success, data) {
    if (success) {
      this.info(LOG_CATEGORIES?.API, `API response: ${endpoint} - SUCCESS`, data);
    } else {
      this.error(LOG_CATEGORIES?.API, `API response: ${endpoint} - FAILED`, data);
    }
  }

  validationError(field, message) {
    this.warn(LOG_CATEGORIES?.VALIDATION, `Validation error: ${field}`, { message });
  }

  // Get all logs for debugging
  getAllLogs() {
    return this.logs;
  }

  // Get logs by category
  getLogsByCategory(category) {
    return this.logs?.filter(log => log?.category === category);
  }

  // Get error logs only
  getErrorLogs() {
    return this.logs?.filter(log => log?.level === LOG_LEVELS?.ERROR);
  }

  // Export logs as JSON
  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    console.log('%c[LOGGER] Logs cleared', 'color: #10B981; font-weight: bold');
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the instance and categories for use
export { logger, LOG_CATEGORIES };
export default logger;